import React, { useContext } from 'react'
import { EventAgendaContext } from '@/component/Context/EventAgendaContext'
import { arrayOf, number, shape, string, oneOf, bool, object } from 'prop-types'
import styles from './AgendaItemCard.module.scss'
import cx from 'classnames'
import Type from '@/component/Primitive/Type'
import SmartLink from '@/component/Primitive/SmartLink'
import moment from 'moment'

const AgendaItemCard = (props) => {
  const {
    title,
    speakers,
    sessionType,
    time,
    startDate,
    endDate,
    day,
    theme,
    shaded,
    align,
    showDatePrefix,
    timezone,
    registerButtonClassName,
    registerAction,
    baseUrl,
    location,
    hideStageLabel,
    openInNewTab
  } = props
  const { handleOpenModal, setEventAgendaModalItem } = useContext(
    EventAgendaContext
  )

  const handleClick = () => {
    setEventAgendaModalItem({
      ...props.agendaItem,
      location: props.agendaItem.location.name,
      sessionType: props.agendaItem.sessionType.name,
      timezone,
      registerButtonClassName,
      registerAction,
      time
    })
    handleOpenModal()
  }

  const isNetworking = () =>
    props?.agendaItem?.sessionType?.name === 'Networking Break'

  const isLive = () => {
    const now = moment()
    const start = moment(startDate)
    const end = moment(endDate)
    return now.isBetween(start, end)
  }

  const Component = (
    <div
      className={cx(
        styles.AgendaItemCard,
        shaded && styles.shaded,
        theme === 'dark' && styles.dark,
        align === 'center' && styles.center,
        isLive() && styles.live,
        hideStageLabel && styles.hideStage
      )}
      role="button"
      tabIndex={0}
      onKeyDown={() => handleClick()}
      {...(!isLive() && {
        onClick: () => handleClick(),
        onKeyDown: () => handleClick()
      })}
    >
      <Type as="p" size="body4" weight="bold" className={styles.Date}>
        {time}
      </Type>
      {!hideStageLabel && (
        <Type as="p" size="body3" weight="regular" className={styles.StageName}>
          {location?.name}
        </Type>
      )}
      <Type as="p" size="body3" weight="regular" className={styles.Name}>
        {title}
      </Type>
      <SpeakersColumn speakers={speakers} isNetworking={isNetworking()} />
      <Type as="p" size="body3" weight="regular" className={styles.SessionType}>
        {sessionType}
      </Type>
    </div>
  )
  return (
    <>
      {showDatePrefix && day && (
        <DateLabel date={startDate} shaded={shaded} align={align} day={day} />
      )}
      {isLive() ? (
        <SmartLink
          to="/events[slug][oslug]"
          as={`/${baseUrl}/live`}
          className={styles.SmartLink}
          {...(openInNewTab && { target: '_blank' })}
        >
          {Component}
        </SmartLink>
      ) : (
        Component
      )}
    </>
  )
}

const SpeakersColumn = ({ speakers, isNetworking }) => {
  if (speakers.length === 0)
    return (
      <div className={styles.Speaker}>
        <Type as="p" size="body3">
          {isNetworking ? '' : 'TBC'}
        </Type>
      </div>
    )
  else if (speakers.length === 1) {
    return (
      <div className={styles.Speaker}>
        <Type as="p" size="body3" weight="bold" className={styles.SpeakerName}>
          {speakers[0].name}
        </Type>
        <Type
          as="p"
          size="body3"
          weight="regular"
          className={styles.CompanyName}
        >
          {speakers[0].companyName}
        </Type>
      </div>
    )
  } else {
    return (
      <div className={styles.SpeakersWrapper}>
        {speakers.map((speaker, index) => (
          <div key={index} className={styles.Speaker}>
            <Type
              as="p"
              size="body3"
              weight="bold"
              className={styles.SpeakerName}
            >
              {speaker.name}
            </Type>
            <span className={styles.SpeakerDivider}>{' - '}</span>
            <Type
              as="p"
              size="body3"
              weight="regular"
              className={styles.CompanyName}
            >
              {speaker.companyName}
            </Type>
          </div>
        ))}
      </div>
    )
  }
}

SpeakersColumn.propTypes = {
  isNetworking: bool,
  speakers: arrayOf(
    shape({
      name: string,
      companyName: string
    })
  )
}

const DateLabel = ({ date, shaded, align, day }) => {
  return (
    <div
      className={cx(
        styles.AgendaItemCard,
        shaded && styles.shaded,
        align === 'center' && styles.center,
        styles.DateLabel
      )}
    >
      <Type as="p" size="body4">
        {moment(date).format('DD MMM')} - Day {day}
      </Type>
    </div>
  )
}

DateLabel.propTypes = {
  date: string,
  day: number,
  shaded: bool,
  align: oneOf(['left', 'center'])
}

AgendaItemCard.propTypes = {
  title: string,
  speakers: arrayOf(
    shape({
      name: string,
      companyName: string,
      role: string
    })
  ),
  align: oneOf(['left', 'center']),
  openInNewTab: bool,
  showDatePrefix: bool,
  startDate: string,
  endDate: string,
  sessionType: string,
  time: string,
  day: number,
  theme: string,
  shaded: bool,
  agendaItem: object,
  timezone: string,
  registerButtonClassName: string,
  registerAction: object,
  baseUrl: string,
  location: object,
  hideStageLabel: bool
}

export default AgendaItemCard
